class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (typeof setupAddonCheckboxHandler === 'function') {
        setupAddonCheckboxHandler('mode-toggle');
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    if (typeof window.__cartReservationTick === 'function') {
      window.__cartReservationTick();
    }
    if (typeof cleanupProtectionProduct === 'function') {
      cleanupProtectionProduct();
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  getResponseItemCount(parsedState) {
    if (typeof parsedState.item_count === 'number') return parsedState.item_count;
    if (Array.isArray(parsedState.items)) {
      return parsedState.items.reduce((total, item) => total + (item.quantity || 0), 0);
    }
    return null;
  }

  syncDrawerEmptyState(parsedState, parsedDoc) {
    const newCartDrawerEl = parsedDoc && parsedDoc.querySelector('cart-drawer');
    if (newCartDrawerEl && newCartDrawerEl.dataset.realItemCount !== undefined) {
      const realCount = parseInt(newCartDrawerEl.dataset.realItemCount, 10) || 0;
      this.classList.toggle('is-empty', realCount === 0);
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', realCount === 0);
      return;
    }

    const itemCount = this.getResponseItemCount(parsedState);
    if (itemCount !== null) {
      this.classList.toggle('is-empty', itemCount === 0);
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', itemCount === 0);
      return;
    }

    if (newCartDrawerEl) {
      this.classList.toggle('is-empty', newCartDrawerEl.classList.contains('is-empty'));
      const newCartDrawerItems = parsedDoc.querySelector('cart-drawer-items');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (newCartDrawerItems && cartDrawerItems) {
        cartDrawerItems.className = newCartDrawerItems.className;
      }
    }
  }

  applySectionUpdates(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (newHTML) {
        sectionElement.innerHTML = newHTML;
      }
    });
  }

  updateContents(parsedState) {
    const cartDrawerSectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    let parsedDoc;

    if (cartDrawerSectionHtml) {
      parsedDoc = new DOMParser().parseFromString(cartDrawerSectionHtml, 'text/html');
      const newInner =
        parsedDoc.querySelector('#CartDrawer .drawer__inner') || parsedDoc.querySelector('.drawer__inner');
      const currentInner = this.querySelector('.drawer__inner');

      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
      }

      if (typeof window.__cartReservationTick === 'function') {
        window.__cartReservationTick();
      }
      if (typeof cleanupProtectionProduct === 'function') {
        cleanupProtectionProduct();
      }

      this.syncDrawerEmptyState(parsedState, parsedDoc);
    } else {
      this.applySectionUpdates(parsedState);
    }

    this.productId = parsedState.id;

    const bubbleHtml = parsedState.sections && parsedState.sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      const bubbleInner = this.getSectionInnerHTML(bubbleHtml);
      if (bubbleEl && bubbleInner) {
        bubbleEl.innerHTML = bubbleInner;
      }
    }

    if (typeof setupAddonCheckboxHandler === 'function') {
      setupAddonCheckboxHandler('mode-toggle');
    }
  }

  renderContents(parsedState) {
    const cartDrawerSectionHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    let parsedDoc;
    const wasActive = this.classList.contains('active');
    const wasAnimate = this.classList.contains('animate');

    if (cartDrawerSectionHtml) {
      parsedDoc = new DOMParser().parseFromString(cartDrawerSectionHtml, 'text/html');
      const newCartDrawerEl = parsedDoc.querySelector('cart-drawer');
      if (newCartDrawerEl) {
        this.className = newCartDrawerEl.className;
        if (wasActive) this.classList.add('active');
        if (wasAnimate) this.classList.add('animate');
      }
      const newCartDrawerItems = parsedDoc.querySelector('cart-drawer-items');
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (newCartDrawerItems && cartDrawerItems) {
        cartDrawerItems.className = newCartDrawerItems.className;
      }
    }

    this.syncDrawerEmptyState(parsedState, parsedDoc);

    this.productId = parsedState.id;
    this.applySectionUpdates(parsedState);

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      if (typeof setupAddonCheckboxHandler === 'function') {
        setupAddonCheckboxHandler('mode-toggle');
      }
      if (!this.classList.contains('active')) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source =
      doc.querySelector(selector) ||
      (selector === '#CartDrawer' ? doc.querySelector('cart-drawer #CartDrawer') : null);

    return source ? source.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
